import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DescriptionIcon from "@mui/icons-material/Description";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  TablePagination,
  Tabs,
  Tab,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  countUnreadNotificationCenter,
  deleteAllNotificationCenter,
  deleteNotificationCenter,
  listNotificationCenter,
  markAllNotificationCenterAsRead,
  markNotificationCenterAsRead,
  notificationCenterQueryKey,
  type NotificationCenterDto,
  type NotificationCenterStatus,
} from "../../api/notification-center";
import { useAuth } from "../../auth";
import { formatNotificationTemplateParams } from "../../utils/notifications";

const notificationsSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
});

const NOTIFICATION_CARD_HEIGHT = 132;

type NotificationFilter = "ALL" | NotificationCenterStatus;

const iconMap = {
  assignment: AssignmentIcon,
  Assignment: AssignmentIcon,
  description: DescriptionIcon,
  Description: DescriptionIcon,
  error: ErrorIcon,
  Error: ErrorIcon,
  info: InfoIcon,
  Info: InfoIcon,
  notifications: NotificationsIcon,
  Notifications: NotificationsIcon,
  task: TaskAltIcon,
  TaskAlt: TaskAltIcon,
  warning: WarningIcon,
  Warning: WarningIcon,
};

const getNotificationIcon = (icon?: string) => {
  const normalizedIcon = icon?.trim();
  if (!normalizedIcon) return NotificationsIcon;

  return (
    iconMap[normalizedIcon as keyof typeof iconMap] ??
    iconMap[normalizedIcon.toLowerCase() as keyof typeof iconMap] ??
    NotificationsIcon
  );
};

const getNotificationTone = (icon?: string) => {
  const normalizedIcon = icon?.trim().toLowerCase();

  switch (normalizedIcon) {
    case "error":
      return { color: "error.main", bg: "error.light" };
    case "warning":
      return { color: "warning.dark", bg: "warning.light" };
    case "info":
      return { color: "info.main", bg: "info.light" };
    case "taskalt":
      return { color: "success.main", bg: "success.light" };
    case "assignment":
    case "description":
      return { color: "primary.main", bg: "primary.light" };
    default:
      return { color: "text.secondary", bg: "action.selected" };
  }
};

const isInternalTargetUrl = (targetUrl?: string) => {
  const normalizedTargetUrl = targetUrl?.trim();
  return Boolean(normalizedTargetUrl?.startsWith("/") && !normalizedTargetUrl.startsWith("//"));
};

const sortNotifications = (items: NotificationCenterDto[]) => {
  return [...items].sort((a, b) => {
    const readWeightA = a.status === "READ" ? 1 : 0;
    const readWeightB = b.status === "READ" ? 1 : 0;
    if (readWeightA !== readWeightB) return readWeightA - readWeightB;

    const dateA = a.notifiedAt ? new Date(a.notifiedAt).getTime() : 0;
    const dateB = b.notifiedAt ? new Date(b.notifiedAt).getTime() : 0;
    return dateB - dateA;
  });
};

export const Route = createFileRoute("/__auth/notifications")({
  component: NotificationsPage,
  validateSearch: (search) => notificationsSearchSchema.parse(search),
  staticData: {
    title: "notifications.title",
  },
});

function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const routeNavigate = Route.useNavigate();
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const [filter, setFilter] = useState<NotificationFilter>("ALL");
  const page = search.page || 1;
  const size = search.size || 10;

  const queryKey = useMemo(
    () => [...notificationCenterQueryKey(currentTenant), "page", page, size],
    [currentTenant, page, size],
  );

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => listNotificationCenter(currentTenant, { page: page - 1, size }),
    enabled: Boolean(currentTenant),
  });

  const { data: unreadCount = 0 } = useQuery({
    queryKey: [...notificationCenterQueryKey(currentTenant), "unread-count"],
    queryFn: () => countUnreadNotificationCenter(currentTenant),
    enabled: Boolean(currentTenant),
  });

  const invalidateNotifications = async () => {
    await queryClient.invalidateQueries({ queryKey: notificationCenterQueryKey(currentTenant) });
  };

  const markAsReadMutation = useMutation({
    mutationFn: (id: number) => markNotificationCenterAsRead(currentTenant, id),
    onSuccess: invalidateNotifications,
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => markAllNotificationCenterAsRead(currentTenant),
    onSuccess: invalidateNotifications,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteNotificationCenter(currentTenant, id),
    onSuccess: invalidateNotifications,
  });

  const deleteAllMutation = useMutation({
    mutationFn: () => deleteAllNotificationCenter(currentTenant),
    onSuccess: async () => {
      routeNavigate({
        search: (prev) => ({ ...prev, page: 1 }),
        replace: true,
      });
      await invalidateNotifications();
    },
  });

  const renderTemplate = (key?: string, fallback?: string, params?: Record<string, string>) => {
    if (!key) return fallback ?? "";
    return t(key, {
      defaultValue: fallback ?? key,
      ...formatNotificationTemplateParams(params, i18n.language),
    });
  };

  const renderTitle = (item: NotificationCenterDto) =>
    renderTemplate(item.titleKey, item.title, item.templateParams);

  const renderMessage = (item: NotificationCenterDto) =>
    renderTemplate(item.messageKey, item.message, item.templateParams);

  const formatTime = (value?: string) => {
    if (!value) return "";

    return new Intl.DateTimeFormat(i18n.language || "pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  };

  const formatMetadata = (item: NotificationCenterDto) => {
    const time = formatTime(item.notifiedAt);
    if (!item.sentBy) {
      return time;
    }

    const sentBy = `${t("notifications.sentBy", "Enviado por")} ${item.sentBy}`;
    return time ? `${time} - ${sentBy}` : sentBy;
  };

  const allNotifications = sortNotifications(data?.content ?? []);
  const notifications =
    filter === "ALL"
      ? allNotifications
      : allNotifications.filter((item) => item.status === filter);

  const totalPages = Math.max(data?.totalPages ?? 1, 1);

  const handlePageChange = (_: unknown, value: number) => {
    routeNavigate({
      search: (prev) => ({ ...prev, page: value }),
      replace: true,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNotificationClick = async (item: NotificationCenterDto) => {
    if (item.status !== "READ" && item.id) {
      await markAsReadMutation.mutateAsync(item.id);
    }

    if (isInternalTargetUrl(item.targetUrl)) {
      void navigate({ to: item.targetUrl!.trim() } as any);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {t("notifications.title", "Notificações")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("notifications.pageSubtitle", "Acompanhe suas notificações em um único lugar.")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip
              color={unreadCount > 0 ? "primary" : "default"}
              label={t("notifications.unreadCount", "{{count}} não lidas", { count: unreadCount })}
            />
            <Button
              startIcon={<DoneAllIcon />}
              disabled={!unreadCount || markAllAsReadMutation.isPending}
              onClick={() => markAllAsReadMutation.mutate()}
            >
              {t("notifications.markAllAsRead", "Marcar todas como lidas")}
            </Button>
            <Button
              color="error"
              startIcon={<DeleteSweepIcon />}
              disabled={!allNotifications.length || deleteAllMutation.isPending}
              onClick={() => deleteAllMutation.mutate()}
            >
              {t("notifications.deleteAll", "Excluir todas")}
            </Button>
          </Box>
        </Box>

        <Card variant="outlined" sx={{ borderRadius: 1 }}>
          <Tabs
            value={filter}
            onChange={(_, value) => {
              setFilter(value);
              routeNavigate({
                search: (prev) => ({ ...prev, page: 1 }),
                replace: true,
              });
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Tab value="ALL" label={t("notifications.filters.all", "Todas")} />
            <Tab value="UNREAD" label={t("notifications.filters.unread", "Não lidas")} />
            <Tab value="READ" label={t("notifications.filters.read", "Lidas")} />
          </Tabs>

          <CardContent sx={{ p: 0 }}>
            {error ? (
              <Alert severity="error" sx={{ m: 2 }}>
                {t("notifications.loadError", "Não foi possível carregar as notificações.")}
              </Alert>
            ) : isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <NotificationsIcon sx={{ fontSize: 42, color: "text.disabled", mb: 1 }} />
                <Typography color="text.secondary">
                  {t("notifications.empty", "Nenhuma notificação.")}
                </Typography>
              </Box>
            ) : (
              <Stack divider={<Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />}>
                {notifications.map((item) => {
                  const NotificationIcon = getNotificationIcon(item.icon);
                  const tone = getNotificationTone(item.icon);
                  const isRead = item.status === "READ";
                  const hasTargetUrl = isInternalTargetUrl(item.targetUrl);

                  return (
                    <Box
                      key={item.id}
                      onClick={() => void handleNotificationClick(item)}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        height: NOTIFICATION_CARD_HEIGHT,
                        px: { xs: 2, sm: 2.5 },
                        py: 2,
                        bgcolor: "background.paper",
                        cursor: isRead && !hasTargetUrl ? "default" : "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "background.paper",
                          color: isRead ? "text.secondary" : tone.color,
                          boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
                        }}
                      >
                        <NotificationIcon fontSize="small" />
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0 }}>
                          <Typography
                            fontWeight={isRead ? 500 : 700}
                            noWrap
                            sx={{ minWidth: 0 }}
                          >
                            {renderTitle(item)}
                          </Typography>
                          {!isRead && (
                            <Chip
                              size="small"
                              color="primary"
                              label={t("notifications.statusUnread", "Não lida")}
                            />
                          )}
                        </Box>
                        <Typography
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            display: "-webkit-box",
                            overflow: "hidden",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                          }}
                        >
                          {renderMessage(item)}
                        </Typography>
                        <Box sx={{ mt: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            title={formatMetadata(item)}
                            sx={{ display: "block", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {formatMetadata(item)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                        {!isRead && item.id && (
                          <Tooltip title={t("notifications.markAsRead", "Marcar como lida")}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={markAsReadMutation.isPending}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  markAsReadMutation.mutate(item.id);
                                }}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {item.id && (
                          <Tooltip title={t("notifications.delete", "Excluir")}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={deleteMutation.isPending}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  deleteMutation.mutate(item.id);
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>

        <TablePagination
          component="div"
          count={data?.totalElements || 0}
          page={Math.min(page - 1, totalPages - 1)}
          onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
          rowsPerPage={size}
          onRowsPerPageChange={(event) => {
            const newSize = parseInt(event.target.value, 10);
            routeNavigate({
              search: (prev) => ({ ...prev, size: newSize, page: 1 }),
              replace: true,
            });
          }}
          labelRowsPerPage={t("common.rowsPerPage", "Linhas por página")}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        />
      </Stack>
    </Container>
  );
}
